import Constants from 'expo-constants';
import { Platform } from 'react-native';

import { InsiderLanguageMap, InsiderServiceConfig } from 'app/services/Insider.types';
import Logger from 'app/services/Logger';

const reinitializeInsiderManager = () => {
  if (typeof window?.Insider?.eventManager?.dispatch === 'function') {
    window.Insider.eventManager.dispatch('init-manager:re-initialize');
  }
};

const InsiderServiceWeb: InsiderServiceConfig = {
  init() {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;

    // disable Insider in SDET environment to prevent permission requests
    if (Constants.expoConfig?.extra?.sdet === 'true') {
      return;
    }

    const insiderConfig = Constants.expoConfig?.extra?.insider;
    const scriptId = 'insider-sdk';

    window.insider_object = window.insider_object || {};

    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;

      script.src = `https://${insiderConfig?.partnerName}.api.useinsider.com/ins.js?id=${insiderConfig?.partnerID}`;
      script.async = true;

      document.head.appendChild(script);

      script.onload = () => {
        Logger.debug('✅ Insider script loaded');
      };
      script.onerror = () => {
        Logger.error('❌ Failed to load Insider script');
      };
    }
  },

  getUser() {
    return window.insider_object?.user || {};
  },

  async handleModifyUser({ userID, email, locale }) {
    const payload: { language: string; email?: string; uuid?: string } = {
      language: InsiderLanguageMap[locale] || 'en_US',
    };

    if (email && userID) {
      payload.email = email;
      payload.uuid = userID;
    }

    window.insider_object.user = { ...this.getUser(), ...payload };

    reinitializeInsiderManager();
  },

  handleHomePageView() {
    this.handleEvent({ type: 'page', value: 'Home' });
  },

  handleListingPageView(taxonomy, params) {
    this.handleEvent(
      {
        type: 'listing',
        value: {
          items: [
            {
              id: params.product_id,
              name: params.page_name,
              taxonomy,
              currency: 'USD',
              unit_price: 1.0,
              unit_sale_price: 1.0,
              url: window.location.href || '',
              product_image_url: params.image_url,
            },
          ],
        },
      },
      { product_id: params.product_id }
    );
  },

  handleProductPageView(product) {
    this.handleEvent({
      type: 'product',
      value: {
        id: product.productID,
        name: product.name,
        taxonomy: product.taxonomy,
        unit_price: product.price,
        unit_sale_price: product.price,
        product_image_url: product.imageURL,
        currency: product.currency,
        locale: product.locale,
        url: window.location.href || '',
      },
    });
  },

  handleAttractionPageView(params) {
    this.handleEvent({
      type: 'events',
      value: {
        event_name: 'attraction_page_view',
        event_params: { product_id: params.attraction_key, name: params.page_name },
      },
    });
  },

  handleOffersPageView(params) {
    this.handleEvent({
      type: 'events',
      value: {
        event_name: 'offer_page_view',
        event_params: { product_id: params.product_id, name: params.page_name },
      },
    });
  },

  handleEvent(eventOrName, params = {}) {
    if (typeof eventOrName !== 'object') return;

    const { type, value } = eventOrName;

    if (type === 'events') {
      window.Insider?.track(type, [value as Record<string, unknown>]);
    } else {
      const customData = Object.keys(params).length > 0 ? { custom: params } : {};
      if (!window.insider_object) {
        window.insider_object = window.insider_object ?? {};
      }
      window.insider_object[type] =
        typeof value === 'object' && !Array.isArray(value)
          ? { ...value, ...customData }
          : {
              type: value,
              ...customData,
            };
    }

    reinitializeInsiderManager();
  },
};

export default InsiderServiceWeb;
